import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import imgRectangle from "../assets/708d0f3eea92211817741311b4a4648b0ea18942.png";
import { 
  Home, 
  Star, 
  Layout, 
  Building, 
  MessageSquare, 
  MapPin, 
  Mail
} from 'lucide-react';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Scroll spy functionality
      const sections = ['home', 'highlights', 'elevations', 'floor-plans', 'reviews', 'location', 'partners', 'contact'];
      const scrollPosition = window.scrollY + 120; // Offset for navigation height
      
      // Special case for the top of the page
      if (window.scrollY < 100) {
        setActiveSection('home');
        return;
      }
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const sectionId = sections[i];
        const element = document.getElementById(sectionId);
        if (element) {
          const elementTop = element.offsetTop;
          
          if (scrollPosition >= elementTop) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = 100; // Account for fixed navigation height
      const elementPosition = element.offsetTop - navHeight;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      
      setActiveSection(sectionId);
    }
  };

  const navItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'highlights', label: 'Highlights', icon: Star },
    { id: 'elevations', label: 'Elevations', icon: Building },
    { id: 'floor-plans', label: 'Floor Plans', icon: Layout },
    { id: 'reviews', label: 'Reviews', icon: MessageSquare },
    { id: 'location', label: 'Location', icon: MapPin },
  { id: 'partners', label: 'Partners', icon: Star },
    { id: 'contact', label: 'Contact', icon: Mail }
  ];

  return (
    <>


      {/* Desktop Navigation - Top */}
      <nav className={`hidden lg:block fixed bottom-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#0c2125]/95 backdrop-blur-md shadow-lg shadow-black/30' 
          : 'bg-[#0c2125]'
      }`}>
        {/* Shining Golden Line - moved to top */}
        <div className="relative h-1 bg-gradient-to-r from-amber-300 via-amber-400 to-amber-300 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200 to-transparent transform translate-x-[-100%] animate-[shimmer_2s_ease-in-out_infinite]"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Layout */}
          <div className="flex items-center justify-between py-4">
            {/* Left Navigation Items */}
            <div className="flex items-center space-x-4">
              {navItems.slice(0, 4).map((item) => {
                const IconComponent = item.icon;
                return (
                  <Button
                    key={item.id}
                    variant="ghost"
                    className={`transition-all duration-200 text-sm px-4 py-4 hover:scale-105 flex flex-col items-center gap-1 rounded-lg min-h-[60px] ${
                      activeSection === item.id 
                        ? 'text-amber-400 bg-amber-400/20 border border-amber-400/30 scale-105' 
                        : 'text-white hover:bg-amber-100/20 hover:text-amber-100'
                    }`}
                    onClick={() => scrollToSection(item.id)}
                  >
                    <IconComponent className="w-8 h-8" />
                    {item.label}
                  </Button>
                );
              })}
            </div>

            {/* Centered Logo */}
            <div className="flex items-center relative z-[60]">
              <div className="w-20 h-20 overflow-hidden rounded-2xl shadow-xl shadow-amber-200/30 border-2 border-amber-400/20 relative z-[60]">
                <img src={imgRectangle} alt="Blucon Sunrise Logo" className="w-full h-full object-cover relative z-[60]" />
              </div>
            </div>

            {/* Right Navigation Items */}
            <div className="flex items-center space-x-4">
              {navItems.slice(4, -1).map((item) => {
                const IconComponent = item.icon;
                return (
                  <Button
                    key={item.id}
                    variant="ghost"
                    className={`transition-all duration-200 text-sm px-4 py-4 hover:scale-105 flex flex-col items-center gap-1 rounded-lg min-h-[60px] ${
                      activeSection === item.id 
                        ? 'text-amber-400 bg-amber-400/20 border border-amber-400/30 scale-105' 
                        : 'text-white hover:bg-amber-100/20 hover:text-amber-100'
                    }`}
                    onClick={() => scrollToSection(item.id)}
                  >
                    <IconComponent className="w-8 h-8" />
                    {item.label}
                  </Button>
                );
              })}
              {/* Contact Button as Special CTA */}
              {(() => {
                const contactItem = navItems.find(item => item.id === 'contact');
                const ContactIcon = contactItem?.icon || Mail;
                return (
                  <Button
                    variant="ghost"
                    className={`transition-all duration-200 text-sm px-4 py-4 hover:scale-105 flex flex-col items-center gap-1 rounded-lg min-h-[60px] ${
                      activeSection === 'contact' 
                        ? 'text-amber-400 bg-amber-400/20 border border-amber-400/30 scale-105' 
                        : 'text-white hover:bg-amber-100/20 hover:text-amber-100'
                    }`}
                    onClick={() => scrollToSection('contact')}
                  >
                    <ContactIcon className="w-8 h-8" />
                    Contact
                  </Button>
                );
              })()}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Logo - Above Navigation */}
      <div className="lg:hidden fixed bottom-16 left-0 right-0 z-[60] flex justify-center pointer-events-none">
        <div className="w-20 h-20 overflow-hidden rounded-xl shadow-lg shadow-amber-200/50 border-2 border-amber-400/30 bg-white pointer-events-auto relative z-[60]">
          <img src={imgRectangle} alt="Blucon Sunrise Logo" className="w-full h-full object-cover relative z-[60]" />
        </div>
      </div>

      {/* Mobile Navigation - Bottom */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0c2125]/95 backdrop-blur-md shadow-lg shadow-black/30 border-t border-amber-400/20">
        <div className="max-w-7xl mx-auto px-2">
          {/* Shining Golden Line - Top */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-300 via-amber-400 to-amber-300 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200 to-transparent transform translate-x-[-100%] animate-[shimmer_2s_ease-in-out_infinite]"></div>
          </div>

          {/* Navigation Menu - Icons Only for Mobile */}
          <div className="flex justify-center items-center gap-1 py-2">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <Button
                  key={item.id}
                  variant="ghost"
                  className={`transition-all duration-200 w-[50px] h-[50px] min-w-[50px] min-h-[50px] rounded-lg hover:scale-110 hover:shadow-lg hover:shadow-amber-300/30 group flex items-center justify-center ${
                    activeSection === item.id 
                      ? 'text-amber-400 bg-amber-400/20 border border-amber-400/30 scale-105' 
                      : 'text-white hover:bg-amber-100/20 hover:text-amber-100'
                  }`}
                  onClick={() => scrollToSection(item.id)}
                  title={item.label}
                >
                  <IconComponent 
                    style={{ width: '24px', height: '24px' }}
                    className="group-hover:animate-pulse transition-all" 
                  />
                </Button>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation;